/**
 * The reviewer's tools — committee, decision, finance clearance, registration
 * and modules. Dropped into the application drawer once an application is real.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users2, Gavel, Wallet, GraduationCap, FileText, CheckCircle2, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';

const pretty = (s) => String(s || '').replace(/_/g, ' ');
const money = (n, c) => `${c || ''} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2 })}`.trim();

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="border-t pt-3 space-y-2" data-testid={testid}>
    <p className="font-semibold text-sm flex items-center gap-1.5">
      <Icon className="h-4 w-4 text-indigo-600" /> {title}
    </p>
    {children}
  </div>
);

const selectClass = 'h-9 w-full rounded-md border border-slate-200 px-2 text-sm capitalize';

const Committee = ({ tenantId, applicationId, invalidate }) => {
  const [form, setForm] = useState({ recommendation: 'recommend',
    reviewer_role: 'admissions_officer', score: '', comments: '' });
  const { data } = useQuery({
    queryKey: ['admissions-reviews', applicationId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/reviews`)).data,
  });
  const qc = useQueryClient();

  const add = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/reviews`,
      { ...form, score: form.score === '' ? null : Number(form.score) }),
    onSuccess: () => {
      toast.success('Recommendation recorded');
      qc.invalidateQueries({ queryKey: ['admissions-reviews', applicationId] });
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not record that'),
  });

  return (
    <Section icon={Users2} title="Review committee" testid="section-committee">
      {(data?.items || []).map((r) => (
        <div key={r.id} className="text-xs text-slate-600" data-testid={`review-${r.id}`}>
          <span className="capitalize font-medium">{pretty(r.recommendation)}</span>
          {r.score !== null && r.score !== undefined && <> · {r.score}/100</>} ·{' '}
          <span className="capitalize">{pretty(r.reviewer_role)}</span> · {r.reviewer}
          {r.comments && <p className="text-slate-500">{r.comments}</p>}
        </div>
      ))}
      {data?.average_score !== null && data?.average_score !== undefined && (
        <p className="text-xs font-medium" data-testid="review-average">
          Average score {data.average_score}/100
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select value={form.recommendation} data-testid="review-recommendation"
                onChange={(e) => setForm({ ...form, recommendation: e.target.value })}
                className={selectClass}>
          {(data?.recommendations || []).map((r) => (
            <option key={r} value={r}>{pretty(r)}</option>
          ))}
        </select>
        <select value={form.reviewer_role} data-testid="review-role"
                onChange={(e) => setForm({ ...form, reviewer_role: e.target.value })}
                className={selectClass}>
          {(data?.reviewer_roles || []).map((r) => (
            <option key={r} value={r}>{pretty(r)}</option>
          ))}
        </select>
        <Input type="number" min="0" max="100" placeholder="Score" value={form.score}
               data-testid="review-score"
               onChange={(e) => setForm({ ...form, score: e.target.value })} />
      </div>
      <Textarea rows={2} placeholder="What stood out?" value={form.comments}
                data-testid="review-comments"
                onChange={(e) => setForm({ ...form, comments: e.target.value })} />
      <Button size="sm" className="h-7 text-xs" onClick={() => add.mutate()}
              disabled={add.isPending} data-testid="review-save">
        Record my recommendation
      </Button>
    </Section>
  );
};

const Decision = ({ tenantId, application, invalidate }) => {
  const applicationId = application.id;
  const [form, setForm] = useState({ outcome: 'accepted', reason: '',
    applicant_note: '', internal_note: '' });
  const [conditions, setConditions] = useState([]);
  const [draft, setDraft] = useState('');
  const qc = useQueryClient();

  const decide = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/decision`,
      { ...form, conditions: conditions.map((label) => ({
        code: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40), label })) }),
    onSuccess: (r) => {
      const reg = r.data?.registration;
      if (reg?.student_number) {
        toast.success(`Accepted and registered as ${reg.student_number}`, {
          description: reg.fees?.total
            ? `Semester registration created and ${reg.fees.currency || ''}${reg.fees.total} of fees raised`
            : 'Semester registration created',
        });
      } else {
        toast.success('Decision recorded — the letter and the email have gone out');
      }
      setConditions([]);
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not record that'),
  });

  const clear = useMutation({
    mutationFn: (code) => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/conditions/${code}/clear`),
    onSuccess: (r) => {
      toast.success(r.data.finalised
        ? (r.data.registration?.student_number
            ? `Last condition cleared — registered as ${r.data.registration.student_number}`
            : 'Last condition cleared — the offer is now firm')
        : `${r.data.outstanding} condition(s) still outstanding`);
      qc.invalidateQueries({ queryKey: ['admissions-application', tenantId, applicationId] });
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not clear that'),
  });

  const decided = application.decision?.outcome;
  const letterUrl = `${process.env.REACT_APP_BACKEND_URL}/api/school/admissions/tenants/${tenantId}/applications/${applicationId}/letter`;

  return (
    <Section icon={Gavel} title="Decision" testid="section-decision">
      {decided && (
        <div className="text-xs space-y-1" data-testid="decision-recorded">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="capitalize">{pretty(decided)}</Badge>
            <span>{application.decision.by_email} · {application.decision.reason}</span>
          </div>
          {application.decision.applicant_note && (
            <p className="text-slate-600">"{application.decision.applicant_note}"</p>
          )}
          <a href={letterUrl} target="_blank" rel="noreferrer"
             className="text-indigo-600 hover:underline" data-testid="decision-letter">
            <FileText className="h-3 w-3 inline mr-1" />Open the letter
          </a>
        </div>
      )}

      {(application.conditions || []).length > 0 && (
        <div className="space-y-1" data-testid="decision-conditions">
          {application.conditions.map((c) => (
            <div key={c.code} className="flex items-center gap-2 text-xs"
                 data-testid={`condition-${c.code}`}>
              {c.status === 'cleared'
                ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                : <span className="h-3.5 w-3.5 rounded-full border border-slate-300" />}
              <span className={c.status === 'cleared' ? 'text-slate-400 line-through' : ''}>
                {c.label}
              </span>
              {c.status !== 'cleared' && (
                <button onClick={() => clear.mutate(c.code)}
                        data-testid={`condition-clear-${c.code}`}
                        className="text-indigo-600 hover:underline ml-auto">
                  Mark as met
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select value={form.outcome} data-testid="decision-outcome"
                onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                className={selectClass}>
          {['accepted', 'conditional', 'waitlisted', 'deferred', 'declined',
            'more_information', 'interview'].map((o) => (
            <option key={o} value={o}>{pretty(o)}</option>
          ))}
        </select>
        <Input placeholder="Reason (for the record)" value={form.reason}
               data-testid="decision-reason"
               onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </div>
      {form.outcome === 'conditional' && (
        <div className="space-y-1" data-testid="decision-condition-builder">
          <div className="flex gap-2">
            <Input placeholder="A condition they must meet" value={draft}
                   data-testid="decision-condition-input"
                   onChange={(e) => setDraft(e.target.value)} />
            <Button size="sm" variant="outline" className="h-9"
                    data-testid="decision-condition-add"
                    onClick={() => { if (draft.trim()) { setConditions([...conditions, draft.trim()]); setDraft(''); } }}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {conditions.map((c, i) => (
            <Badge key={i} variant="outline" className="text-[10px] mr-1">
              {c}
              <button className="ml-1" onClick={() => setConditions(
                conditions.filter((_, j) => j !== i))}>
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Textarea rows={2} placeholder="What the applicant will read" value={form.applicant_note}
                data-testid="decision-applicant-note"
                onChange={(e) => setForm({ ...form, applicant_note: e.target.value })} />
      <Textarea rows={2} placeholder="Internal note — never shown to the applicant"
                value={form.internal_note} data-testid="decision-internal-note"
                onChange={(e) => setForm({ ...form, internal_note: e.target.value })} />
      <Button size="sm" className="h-7 text-xs" onClick={() => decide.mutate()}
              disabled={decide.isPending} data-testid="decision-save">
        Record the decision
      </Button>
    </Section>
  );
};

const FinanceAndRegistration = ({ tenantId, application, invalidate }) => {
  const applicationId = application.id;
  const [payment, setPayment] = useState({ kind: 'registration_deposit', amount: '' });
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admissions-readiness', applicationId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/registration-readiness`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admissions-readiness', applicationId] });
    invalidate();
  };

  const pay = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/payments`,
      { ...payment, amount: Number(payment.amount) }),
    onSuccess: () => { toast.success('Payment recorded'); setPayment({ ...payment, amount: '' }); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not record that'),
  });

  const register = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/register`, {}),
    onSuccess: (r) => { toast.success(`Registered as ${r.data.student_number}`); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not register them'),
  });

  const checkin = useMutation({
    mutationFn: () => api.post(
      `/school/admissions/tenants/${tenantId}/applications/${applicationId}/orientation-checkin`,
      { steps: ['id_verified', 'orientation_attended'], issue_qr: true }),
    onSuccess: () => { toast.success('Checked in — they are now an active student'); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not check them in'),
  });

  if (!data) return <Skeleton className="h-24 w-full" />;
  const ccy = data.finance?.currency;

  return (
    <Section icon={Wallet} title="Finance clearance & registration"
             testid="section-registration">
      {(data.finance?.lines || []).map((line) => (
        <div key={line.label} className="flex items-center gap-2 text-xs"
             data-testid={`finance-line-${line.label.toLowerCase().replace(/ /g, '-')}`}>
          {line.cleared
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            : <span className="h-3.5 w-3.5 rounded-full border border-amber-400" />}
          <span className="flex-1">{line.label}</span>
          <span className="text-slate-500">
            {money(line.paid, ccy)} of {money(line.required, ccy)}
            {line.outstanding > 0 && ` · ${money(line.outstanding, ccy)} to go`}
          </span>
        </div>
      ))}
      {data.finance?.sponsor?.credit_applied > 0 && (
        <p className="text-[11px] text-slate-500" data-testid="finance-sponsor">
          {data.finance.sponsor.name || 'A sponsor'} covers{' '}
          {money(data.finance.sponsor.credit_applied, ccy)}.
        </p>
      )}

      <div className="flex gap-2">
        <select value={payment.kind} data-testid="payment-kind"
                onChange={(e) => setPayment({ ...payment, kind: e.target.value })}
                className={selectClass}>
          <option value="registration_deposit">Registration deposit</option>
          <option value="application_fee">Application fee</option>
          <option value="other">Other</option>
        </select>
        <Input type="number" min="0" step="0.01" placeholder="Amount"
               value={payment.amount} data-testid="payment-amount"
               onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
        <Button size="sm" variant="outline" className="h-9 whitespace-nowrap"
                data-testid="payment-save" disabled={pay.isPending || !payment.amount}
                onClick={() => pay.mutate()}>
          Record
        </Button>
      </div>

      <div className="space-y-1">
        {data.gates.map((g) => (
          <div key={g.key} className="flex items-center gap-2 text-xs"
               data-testid={`gate-${g.key}`}>
            {g.cleared
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              : <span className="h-3.5 w-3.5 rounded-full border border-slate-300" />}
            <span>{g.label}</span>
            {g.detail && <span className="text-slate-400">— {g.detail}</span>}
          </div>
        ))}
      </div>

      {data.already_registered ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-emerald-100 text-emerald-700" data-testid="student-number">
            {data.student_number}
          </Badge>
          {application.enrolment_status !== 'active' && (
            <Button size="sm" className="h-7 text-xs" data-testid="orientation-checkin"
                    disabled={checkin.isPending} onClick={() => checkin.mutate()}>
              Orientation check-in
            </Button>
          )}
          {application.enrolment_status === 'active' && (
            <span className="text-xs text-emerald-700">Active student</span>
          )}
        </div>
      ) : (
        <Button size="sm" className="h-7 text-xs" data-testid="register-student"
                disabled={!data.ready || register.isPending}
                onClick={() => register.mutate()}>
          {data.ready ? 'Register as a student' : `Blocked: ${data.blocking.join(', ')}`}
        </Button>
      )}
    </Section>
  );
};

const Modules = ({ tenantId, application }) => {
  const studentId = application.student_id;
  const termId = application.term_id;
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admissions-modules', studentId, termId],
    queryFn: async () => (await api.get(
      `/school/admissions/tenants/${tenantId}/students/${studentId}/available-modules`,
      { params: { term_id: termId } })).data,
    enabled: !!studentId && !!termId,
  });

  const add = useMutation({
    mutationFn: (offeringId) => api.post(
      `/school/admissions/tenants/${tenantId}/students/${studentId}/module-registrations`,
      { term_id: termId, offering_ids: [offeringId] }),
    onSuccess: (r) => {
      const refused = r.data.refused?.[0];
      if (refused) toast.error(`Not registered — ${refused.reasons.join('; ')}`);
      else toast.success(`Registered · ${r.data.credits} credits this semester`);
      qc.invalidateQueries({ queryKey: ['admissions-modules', studentId, termId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not register that'),
  });

  if (!studentId) return null;
  if (!data) return <Skeleton className="h-20 w-full" />;

  return (
    <Section icon={GraduationCap} title="Modules this semester" testid="section-modules">
      <p className="text-xs text-slate-500">
        {data.credits_taken} of {data.max_credits} credits taken
      </p>
      {!data.items.length ? (
        <p className="text-xs text-slate-500">
          No modules are offered for this semester yet.
        </p>
      ) : data.items.map((m) => (
        <div key={m.id} className="flex items-center gap-2 text-xs py-0.5"
             data-testid={`module-${m.module_code}`}>
          <span className="font-mono">{m.module_code}</span>
          <span className="flex-1 truncate">{m.module_name}</span>
          <span className="text-slate-400">{m.credits} cr</span>
          {m.places_left !== null && (
            <span className="text-slate-400">{m.places_left} left</span>
          )}
          {m.eligible ? (
            <button onClick={() => add.mutate(m.id)} disabled={add.isPending}
                    data-testid={`module-add-${m.module_code}`}
                    className="text-indigo-600 hover:underline">Register</button>
          ) : (
            <span className="text-amber-700 max-w-[45%] text-right"
                  data-testid={`module-blocked-${m.module_code}`}>
              {m.reasons[0]}
            </span>
          )}
        </div>
      ))}
    </Section>
  );
};

export const AdmissionsDecisionPanel = ({ tenantId, application, invalidate }) => (
  <div className="space-y-3">
    <Committee tenantId={tenantId} applicationId={application.id} invalidate={invalidate} />
    <Decision tenantId={tenantId} application={application} invalidate={invalidate} />
    <FinanceAndRegistration tenantId={tenantId} application={application}
                            invalidate={invalidate} />
    <Modules tenantId={tenantId} application={application} />
  </div>
);

export default AdmissionsDecisionPanel;
